/**
 * ①概況（08 §5.1・FR-70）＝クロステナントの「今どうなっているか」を一目で。
 *
 * 出すのは**集計値だけ**（08 §8 の伏字既定＝原文・PII は運営に見せない）。
 * 「要確認あり」「AI 下書き失敗」「滞留」は 0 より大きいと色が付く＝運営が最初に見る場所。
 */

import { ops, type OpsOverview } from "@kenzai/api-client";
import { Badge, type BadgeTone, Card, ErrorNote, formatDateTime, PageHeader, Spinner } from "@kenzai/ui";
import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";

export function Overview() {
  const q = useQuery({
    queryKey: ["ops-overview"],
    queryFn: () => ops.overview(),
    refetchInterval: 60_000, // 概況は 1 分ごとに追従（運営が開きっぱなしにする画面）
  });

  return (
    <>
      <PageHeader title="概況" subtitle="全テナントの状況を集計で見ます（原文・個人情報は表示しません）。" />
      {q.isLoading ? (
        <Spinner />
      ) : q.isError ? (
        <ErrorNote error={q.error} />
      ) : (
        <OverviewBody d={q.data!} />
      )}
    </>
  );
}

function OverviewBody({ d }: { d: OpsOverview }) {
  return (
    <div style={{ display: "grid", gap: "var(--kz-space-5)" }}>
      <section style={grid}>
        <Metric
          testid="ov-tenants-total"
          label="テナント"
          value={d.tenants_total}
          hint={
            <span style={{ display: "flex", gap: "var(--kz-space-1)", flexWrap: "wrap" }}>
              <Badge tone="primary">試用 {d.tenants_trial}</Badge>
              <Badge tone="ok">稼働 {d.tenants_active}</Badge>
              <Badge tone="neutral">停止 {d.tenants_suspended}</Badge>
            </span>
          }
        />
        <Metric testid="ov-orders-today" label="今日の注文" value={d.orders_today} hint="本日 JST の受注件数" />
      </section>

      <section style={grid}>
        <Metric
          testid="ov-unresolved"
          label="要確認あり"
          value={d.unresolved_orders}
          tone={d.unresolved_orders > 0 ? "warn" : "ok"}
          hint="未解消のフラグが残る注文（取消を除く）"
        />
        <Metric
          testid="ov-ai-failed"
          label="AI 下書き失敗"
          value={d.ai_failed_orders}
          tone={d.ai_failed_orders > 0 ? "danger" : "ok"}
          hint="AI が下書きを作れず人手待ちの注文"
        />
        <Metric
          testid="ov-stalled"
          label="滞留"
          value={d.stalled_orders}
          tone={d.stalled_orders > 0 ? "warn" : "ok"}
          hint="未確認のまま前日以前から残る注文"
        />
      </section>

      <p style={{ fontSize: "var(--kz-font-size-sm)", color: "var(--kz-color-ink-muted)", margin: 0 }}>
        集計時刻：<span className="kz-num">{formatDateTime(d.generated_at)}</span>
      </p>
    </div>
  );
}

const grid: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: "var(--kz-space-4)",
};

const TONE_ACCENT: Record<BadgeTone, string> = {
  neutral: "var(--kz-color-ink)",
  primary: "var(--kz-color-primary)",
  warn: "var(--kz-status-warn)",
  danger: "var(--kz-status-danger)",
  ok: "var(--kz-color-ink)",
  // 運営の数値カードでは使わないが、BadgeTone の全値を埋めておく（Record が網羅を強制する）
  transit: "var(--kz-status-transit)",
  role: "var(--kz-role-admin)",
};

function Metric({
  testid,
  label,
  value,
  hint,
  tone = "neutral",
}: {
  testid: string;
  label: string;
  value: number;
  hint?: ReactNode;
  tone?: BadgeTone;
}) {
  return (
    <Card>
      <div style={{ display: "grid", gap: "var(--kz-space-2)" }}>
        <div style={{ fontSize: "var(--kz-font-size-sm)", color: "var(--kz-color-ink-muted)" }}>{label}</div>
        <div
          data-testid={testid}
          className="kz-num"
          style={{ fontSize: "2rem", fontWeight: 700, lineHeight: 1, color: TONE_ACCENT[tone] }}
        >
          {value}
        </div>
        {hint && (
          <div style={{ fontSize: "var(--kz-font-size-sm)", color: "var(--kz-color-ink-muted)" }}>{hint}</div>
        )}
      </div>
    </Card>
  );
}
